import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { SessionError } from '@typesApp/AppEvent'
import { SubmitErrorResponse } from '@typesApp/SubmitResponse'

const STATUS_CODES = {
  GATEWAY_TIMEOUT: 504,
}

const CUSTOM_ERROR = {
  STATUS: 'CUSTOM_ERROR',
  DATA: {
    GENERIC_ERROR: 'GENERIC_ERROR',
    SESSION_EXPIRED: 'SESSION_EXPIRED',
    INVALID_CUSTOMER: 'INVALID_CUSTOMER',
    CUSTOMER_CHANGED: 'CUSTOMER_CHANGED',
  },
} as const

type FetchErrorData = {
  error?: string
  data?:
    | string
    | {
        error?: string
        message?: string
      }
}

type GenericErrorData = {
  error?: string
}

export class InvalidCustomerError extends Error {}

export class CustomerChangedError extends Error {}

type InvalidCustomerFetchError = FetchBaseQueryError & {
  cause?: InvalidCustomerError
}

type CustomerChangedFetchError = FetchBaseQueryError & {
  cause?: CustomerChangedError
}

export const isGatewayTimeout = (errorOrStatus: unknown | number | undefined): boolean => {
  return (
    errorOrStatus === STATUS_CODES.GATEWAY_TIMEOUT ||
    (isFetchBaseError(errorOrStatus) && errorOrStatus.status === STATUS_CODES.GATEWAY_TIMEOUT)
  )
}

export const isErrorWithClaimRecord = (error?: unknown): error is SubmitErrorResponse => {
  const errorResponse = error as SubmitErrorResponse
  return Boolean(errorResponse?.status && errorResponse?.data)
}

export const isSessionExpiredError = (error?: SessionError): boolean => {
  return error?.errorCode === CUSTOM_ERROR.DATA.SESSION_EXPIRED || error?.errorKey === CUSTOM_ERROR.DATA.SESSION_EXPIRED
}

export const isInvalidCustomerError = (error?: unknown): boolean => {
  if (error instanceof InvalidCustomerError) return true

  return isFetchBaseError(error) && error?.data === CUSTOM_ERROR.DATA.INVALID_CUSTOMER
}

export const isCustomerChangedError = (error?: unknown): boolean => {
  if (error instanceof CustomerChangedError) return true

  return isFetchBaseError(error) && error?.data === CUSTOM_ERROR.DATA.CUSTOMER_CHANGED
}

export const isFetchBaseError = (error?: unknown): error is FetchBaseQueryError => {
  const fetchError = error as FetchBaseQueryError
  return Boolean(fetchError?.status)
}

export const isUnauthorizedError = (error?: unknown): boolean => {
  return isFetchBaseError(error) && error.status === 401
}

export const isResourceNotFoundError = (error?: unknown): boolean => {
  return isFetchBaseError(error) && error.status === 404
}

export const isConflictError = (error?: unknown): boolean => {
  return isFetchBaseError(error) && error.status === 409
}

export const isInternalServerError = (error?: unknown): boolean => {
  return isFetchBaseError(error) && error.status === 500
}

const isFetchBaseErrorData = (error?: unknown): error is FetchErrorData => {
  return isFetchBaseError(error)
}

const isGenericErrorData = (error?: unknown): error is GenericErrorData => {
  return Boolean((error as GenericErrorData)?.error)
}

export const getErrorMessage = (error?: unknown): string | undefined => {
  if (error instanceof Error) return error.message ?? error.name

  if (isFetchBaseErrorData(error)) {
    if (typeof error.error === 'string') return error.error
    if (typeof error.data === 'string') return error.data

    if (typeof error.data === 'object') {
      if (typeof error.data?.error === 'string') return error.data.error
      if (typeof error.data?.message === 'string') return error.data.message
      return safeStringify(error.data)
    }
  }

  if (error && typeof error === 'object') {
    if (isGenericErrorData(error)) {
      return error.error
    }
    return Object.keys(error).length ? safeStringify(error) : undefined
  }

  if (typeof error === 'string') {
    return error
  }

  return error ? `${error}` : undefined
}

const safeStringify = (data: object): string | undefined => {
  try {
    return JSON.stringify(data)
  } catch (e) {
    return undefined
  }
}

export const createInvalidCustomerFetchError = (
  error: string,
  cause?: InvalidCustomerError
): InvalidCustomerFetchError => {
  return {
    status: CUSTOM_ERROR.STATUS,
    data: CUSTOM_ERROR.DATA.INVALID_CUSTOMER,
    error,
    cause,
  }
}

export const createCustomerChangedFetchError = (
  error: string,
  cause?: CustomerChangedError
): CustomerChangedFetchError => {
  return {
    status: CUSTOM_ERROR.STATUS,
    data: CUSTOM_ERROR.DATA.CUSTOMER_CHANGED,
    error,
    cause,
  }
}

export const createGenericFetchError = (error: string): FetchBaseQueryError => {
  return {
    status: CUSTOM_ERROR.STATUS,
    data: CUSTOM_ERROR.DATA.GENERIC_ERROR,
    error,
  }
}
